import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Header } from "../components/Header";
import { FeedbackLink } from "../components/FeedbackLink";
import { ExtensionWaitlist } from "../components/ExtensionWaitlist";
import { PlaybackBar } from "../components/PlaybackBar";
import { SmartTimeline } from "../components/SmartTimeline";
import { SiteFooter } from "../components/SiteFooter";
import { UrlForm } from "../components/UrlForm";
import { VideoShelf } from "../components/VideoShelf";
import { apiRequest } from "../lib/api";
import { getAnonymousVisitorId } from "../lib/anonymousVisitor";
import { adaptiveEstimationRates, DEFAULT_PLAYBACK_MODE, DEFAULT_SPEED_LIMITS, findSegmentIndex, findSponsorSkipEnd, PLAYBACK_MODES, resolveSegmentRate, speedLimitsForMode, YOUTUBE_SPEED_OPTIONS } from "../lib/playback";
import { getModePlaybackPlan } from "../lib/modePlanCache";
import { shareVideoLink, versionedShareUrl } from "../lib/share";
import { useYouTubePlayer } from "../hooks/useYouTubePlayer";
import { usePlaybackEngagement } from "../hooks/usePlaybackEngagement";

const ANALYSIS_LOADING_NOTES = [
  "Finding captions",
  "Sorting tangents",
  "Classifying sections",
  "Building timeline",
  "Calculating runtime",
  "Almost there",
];

function storedPlaybackPreferences() {
  const fallback = { mode: DEFAULT_PLAYBACK_MODE, speeds: { ...DEFAULT_SPEED_LIMITS } };
  try {
    const mode = window.localStorage.getItem("theofast-playback-mode");
    const savedSpeeds = JSON.parse(window.localStorage.getItem("theofast-playback-speeds") || "null");
    const validMode = mode === "custom" || Object.prototype.hasOwnProperty.call(PLAYBACK_MODES, mode) ? mode : DEFAULT_PLAYBACK_MODE;
    if (validMode !== "custom") return { mode: validMode, speeds: speedLimitsForMode(validMode) };
    if (savedSpeeds && YOUTUBE_SPEED_OPTIONS.includes(savedSpeeds.normal) && YOUTUBE_SPEED_OPTIONS.includes(savedSpeeds.fast)) {
      return { mode: "custom", speeds: { normal: savedSpeeds.normal, fast: savedSpeeds.fast } };
    }
    return fallback;
  } catch {
    return fallback;
  }
}

export function PlayerPage({ videoId, theme, onToggleTheme, onNavigate }) {
  const [analysis, setAnalysis] = useState({ status: "loading", video: null, plan: null, message: "Analyzing available captions…" });
  const [loadingNoteIndex, setLoadingNoteIndex] = useState(0);
  const [adaptiveEnabled, setAdaptiveEnabled] = useState(true);
  const [overridden, setOverridden] = useState(false);
  const [segmentSpeedOverride, setSegmentSpeedOverride] = useState(null);
  const [speedMode, setSpeedMode] = useState(() => storedPlaybackPreferences().mode);
  const [speedLimits, setSpeedLimits] = useState(() => storedPlaybackPreferences().speeds);
  const [modeProcessing, setModeProcessing] = useState(false);
  const [pendingSpeedMode, setPendingSpeedMode] = useState(null);
  const [seekTime, setSeekTime] = useState(null);
  const [shareState, setShareState] = useState("Share");
  const [immersive, setImmersive] = useState(false);
  const [immersiveLayout, setImmersiveLayout] = useState("portrait");
  const [immersiveControlsVisible, setImmersiveControlsVisible] = useState(true);
  const immersiveCardRef = useRef(null);
  const immersiveHideTimerRef = useRef(null);
  const immersiveControlsVisibleRef = useRef(true);
  const revealedByHoverRef = useRef(false);
  const immersiveGestureRef = useRef(null);
  const ignoreNextEmptyClickRef = useRef(false);
  const lastRequestRef = useRef("");
  const modePlanCacheRef = useRef(new Map());
  const modeProcessTimerRef = useRef(null);
  const lastAppliedRef = useRef({ segment: -1, speed: 0 });
  const hasAppliedAdaptiveRef = useRef(false);
  const adaptiveStateRef = useRef(true);
  const desiredAdaptiveRateRef = useRef(null);
  const manualRateRef = useRef(1);
  const resumeRateGraceUntilRef = useRef(0);
  const resumeExpectedRateRef = useRef(null);
  const resumeRateTimerRef = useRef(null);

  const handleRateChange = useCallback((rate, origin) => {
    const differsFromAdaptiveTarget = desiredAdaptiveRateRef.current == null
      || Math.abs(rate - desiredAdaptiveRateRef.current) >= 0.01;
    const rateExpectedAcrossLifecycle = document.visibilityState === "hidden"
      ? desiredAdaptiveRateRef.current ?? manualRateRef.current
      : resumeExpectedRateRef.current ?? desiredAdaptiveRateRef.current ?? manualRateRef.current;
    const lifecycleReset = (document.visibilityState === "hidden" || Date.now() < resumeRateGraceUntilRef.current)
      && Math.abs(rate - 1) < 0.01
      && Math.abs(rateExpectedAcrossLifecycle - 1) >= 0.01;
    if (origin === "user" && !lifecycleReset) {
      manualRateRef.current = rate;
      if (differsFromAdaptiveTarget && hasAppliedAdaptiveRef.current && adaptiveStateRef.current) setOverridden(true);
    }
    if (origin === "unsupported") setOverridden(true);
  }, []);
  const player = useYouTubePlayer(videoId, { onRateChange: handleRateChange });
  const analyticsPreferencesRef = useRef({});
  analyticsPreferencesRef.current = {
    speedLimits,
    playbackMode: speedMode,
    availableRates: player.ready ? player.availableRates : undefined,
  };
  const planPending = Boolean(videoId && ["loading", "awaiting_player_metadata"].includes(analysis.status));

  useEffect(() => {
    if (!planPending) {
      setLoadingNoteIndex(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setLoadingNoteIndex((current) => (current + 1) % ANALYSIS_LOADING_NOTES.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [planPending]);

  const setImmersiveControls = useCallback((visible) => {
    immersiveControlsVisibleRef.current = visible;
    setImmersiveControlsVisible(visible);
    window.clearTimeout(immersiveHideTimerRef.current);
    if (!visible) return;
    const hideWhenIdle = () => {
      const focusedInControls = immersiveCardRef.current?.querySelector(".immersive-toolbar")?.contains(document.activeElement)
        || immersiveCardRef.current?.querySelector(".playback-bar")?.contains(document.activeElement);
      if (focusedInControls) {
        immersiveHideTimerRef.current = window.setTimeout(hideWhenIdle, 1800);
        return;
      }
      immersiveControlsVisibleRef.current = false;
      setImmersiveControlsVisible(false);
    };
    immersiveHideTimerRef.current = window.setTimeout(hideWhenIdle, 3200);
  }, []);

  const wakeImmersiveControls = useCallback(() => {
    if (immersive) setImmersiveControls(true);
  }, [immersive, setImmersiveControls]);
  const wakeControlsFromChildFocus = (event) => {
    if (event.target !== event.currentTarget) wakeImmersiveControls();
  };

  const toggleImmersiveControlsFromEmpty = (event) => {
    if (!immersive) return;
    if (event.target.closest?.("button,input,select,textarea,a,.video-frame,.playback-bar,.immersive-toolbar")) return;
    if (ignoreNextEmptyClickRef.current) {
      ignoreNextEmptyClickRef.current = false;
      return;
    }
    revealedByHoverRef.current = false;
    try { immersiveCardRef.current?.focus({ preventScroll: true }); } catch { immersiveCardRef.current?.focus(); }
    setImmersiveControls(!immersiveControlsVisibleRef.current);
  };

  const toggleImmersiveControlsFromVideo = () => {
    if (!immersive) return;
    if (ignoreNextEmptyClickRef.current) {
      ignoreNextEmptyClickRef.current = false;
      return;
    }
    revealedByHoverRef.current = false;
    setImmersiveControls(!immersiveControlsVisibleRef.current);
  };

  const handleImmersivePointerDown = (event) => {
    const videoWakeSurface = event.target.closest?.(".immersive-video-tap-surface");
    if (!immersive || (!videoWakeSurface && event.target.closest?.("button,input,select,textarea,a,.video-frame,.playback-bar,.immersive-toolbar"))) return;
    immersiveGestureRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Some embedded browsers do not support pointer capture. */ }
  };

  const handleImmersivePointerUp = (event) => {
    const start = immersiveGestureRef.current;
    immersiveGestureRef.current = null;
    if (!start || start.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (distance >= 80) {
      ignoreNextEmptyClickRef.current = true;
      exitImmersive();
    }
  };

  const revealImmersiveControlsOnHover = () => {
    if (!immersiveControlsVisibleRef.current) revealedByHoverRef.current = true;
    wakeImmersiveControls();
  };

  const exitImmersive = useCallback(() => {
    const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
    if ((document.fullscreenElement || document.webkitFullscreenElement) && exitFullscreen) {
      try {
        const result = exitFullscreen.call(document);
        result?.catch?.(() => {});
      } catch { /* Keep the custom player overlay as a fallback. */ }
    }
    setImmersive(false);
  }, []);

  useEffect(() => {
    if (!immersive) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    immersiveCardRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") exitImmersive();
      else wakeImmersiveControls();
      if (event.key !== "Tab") return;
      const buttons = immersiveCardRef.current?.querySelectorAll("button:not(:disabled)");
      if (!buttons?.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) setImmersive(false);
    };
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      window.clearTimeout(immersiveHideTimerRef.current);
      window.requestAnimationFrame(() => document.querySelector(".playback-bar__fullscreen")?.focus());
    };
  }, [exitImmersive, immersive, wakeImmersiveControls]);

  useEffect(() => {
    if (!immersive || !player.ready) return undefined;
    const iframe = immersiveCardRef.current?.querySelector(".video-frame iframe");
    if (!iframe) return undefined;
    // YouTube's cross-origin iframe swallows pointer events, but focusing it
    // when tapped is observable by the containing document.
    const onPlayerFocus = () => wakeImmersiveControls();
    iframe.addEventListener("focus", onPlayerFocus);
    return () => iframe.removeEventListener("focus", onPlayerFocus);
  }, [immersive, player.ready, wakeImmersiveControls]);

  useEffect(() => {
    if (immersive) wakeImmersiveControls();
  }, [immersive, player.playing, wakeImmersiveControls]);

  useEffect(() => {
    if (!immersive) {
      immersiveControlsVisibleRef.current = true;
      setImmersiveControlsVisible(true);
      window.clearTimeout(immersiveHideTimerRef.current);
      return undefined;
    }
    setImmersiveControls(true);
    return () => window.clearTimeout(immersiveHideTimerRef.current);
  }, [immersive, setImmersiveControls]);

  useEffect(() => () => window.clearTimeout(modeProcessTimerRef.current), []);

  const enterImmersive = () => {
    ignoreNextEmptyClickRef.current = false;
    immersiveGestureRef.current = null;
    setImmersiveLayout("portrait");
    const element = immersiveCardRef.current;
    const requestFullscreen = element?.requestFullscreen || element?.webkitRequestFullscreen;
    if (requestFullscreen) {
      try {
        const result = requestFullscreen.call(element, { navigationUI: "hide" });
        result?.catch?.(() => {});
      } catch { /* Older Safari keeps the custom in-page immersive layout. */ }
    }
    setImmersive(true);
  };

  const runAnalysis = useCallback(async (duration, forceFresh = false) => {
    if (!videoId) return;
    const requestKey = `${videoId}:${duration ? Math.round(duration * 10) / 10 : "metadata"}:${forceFresh ? "fresh" : "cached"}`;
    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;
    setAnalysis((current) => ({ ...current, status: current.plan && !forceFresh ? current.status : "loading", message: "Analyzing available captions…" }));
    try {
      const result = await apiRequest("/api/theofast/videos/analyze", {
        method: "POST",
        body: JSON.stringify({
          input: videoId,
          ...(duration ? { duration } : {}),
          ...(forceFresh ? { forceFresh: true } : {}),
          anonymousId: getAnonymousVisitorId(),
          ...analyticsPreferencesRef.current,
        }),
      });
      setAnalysis({ status: result.status, video: result.video || null, plan: result.plan || null, message: result.message || "", cached: result.cached });
    } catch (error) {
      setAnalysis({ status: error.code === "TRANSCRIPT_UNAVAILABLE" ? "unsupported" : "error", video: null, plan: null, message: error.message, code: error.code });
    }
  }, [videoId]);

  useEffect(() => {
    lastRequestRef.current = "";
    window.clearTimeout(modeProcessTimerRef.current);
    setModeProcessing(false);
    setPendingSpeedMode(null);
    setAnalysis({ status: "loading", video: null, plan: null, message: "Analyzing available captions…" });
    setAdaptiveEnabled(true);
    setOverridden(false);
    setSegmentSpeedOverride(null);
    setSeekTime(null);
    setShareState("Share");
    setImmersive(false);
    lastAppliedRef.current = { segment: -1, speed: 0 };
    hasAppliedAdaptiveRef.current = false;
    adaptiveStateRef.current = true;
    desiredAdaptiveRateRef.current = null;
    manualRateRef.current = 1;
    resumeExpectedRateRef.current = null;
    resumeRateGraceUntilRef.current = 0;
    window.clearTimeout(resumeRateTimerRef.current);
  }, [videoId]);

  useEffect(() => { if (videoId) runAnalysis(); }, [runAnalysis, videoId]);
  useEffect(() => {
    if (videoId && player.duration > 0 && analysis.status === "awaiting_player_metadata") runAnalysis(player.duration);
  }, [analysis.status, player.duration, runAnalysis, videoId]);

  const estimationRates = useMemo(() => adaptiveEstimationRates(player.availableRates), [player.availableRates]);
  const effectivePlan = useMemo(() => getModePlaybackPlan(videoId, analysis.plan, speedMode, speedLimits, estimationRates, modePlanCacheRef.current), [analysis.plan, estimationRates, speedLimits, speedMode, videoId]);
  const currentSegmentIndex = effectivePlan ? findSegmentIndex(effectivePlan.segments, player.currentTime) : -1;
  const currentSegment = effectivePlan?.segments[currentSegmentIndex];
  const playerReady = player.ready;
  const setPlayerRate = player.setRate;
  const refreshPlayer = player.refresh;

  useEffect(() => {
    if (!playerReady || !effectivePlan || !adaptiveEnabled || overridden || currentSegmentIndex < 0) return;
    const nextSpeed = resolveSegmentRate(effectivePlan.segments, currentSegmentIndex, segmentSpeedOverride);
    const previous = lastAppliedRef.current;
    const targetWasApplied = previous.segment === currentSegmentIndex && Math.abs(previous.speed - nextSpeed) < 0.01;
    const playerReportsTarget = Math.abs(player.playbackRate - nextSpeed) < 0.01;
    if (targetWasApplied && playerReportsTarget) return;
    lastAppliedRef.current = { segment: currentSegmentIndex, speed: nextSpeed };
    desiredAdaptiveRateRef.current = nextSpeed;
    hasAppliedAdaptiveRef.current = true;
    setPlayerRate(nextSpeed, "adaptive");
  }, [adaptiveEnabled, currentSegmentIndex, effectivePlan, overridden, player, player.playbackRate, player.playing, playerReady, segmentSpeedOverride, setPlayerRate]);

  useEffect(() => {
    const restorePlaybackRate = () => {
      if (document.visibilityState === "hidden") return;
      window.clearTimeout(resumeRateTimerRef.current);
      if (!playerReady) return;

      // After a long suspension, mobile YouTube can report the intended rate
      // while its media pipeline is actually back at 1×. Reassert the rate
      // across the iframe's wake-up window instead of trusting that stale value.
      const retryDelays = [160, 500, 1200, 2500, 5000];
      let attempt = 0;
      const verifyExpectedRate = () => {
        if (document.visibilityState === "hidden" || !playerReady) return;
        const snapshot = refreshPlayer();
        if (snapshot) {
          const segmentIndex = effectivePlan ? findSegmentIndex(effectivePlan.segments, snapshot.currentTime) : -1;
          const adaptiveRate = effectivePlan ? resolveSegmentRate(effectivePlan.segments, segmentIndex, segmentSpeedOverride) : null;
          const expectedRate = adaptiveEnabled && !overridden && adaptiveRate
            ? adaptiveRate
            : manualRateRef.current;
          resumeExpectedRateRef.current = expectedRate;
          resumeRateGraceUntilRef.current = Date.now() + 6000;

          if (adaptiveEnabled && !overridden && adaptiveRate) {
            desiredAdaptiveRateRef.current = adaptiveRate;
            hasAppliedAdaptiveRef.current = true;
            lastAppliedRef.current = { segment: segmentIndex, speed: adaptiveRate };
            setPlayerRate(adaptiveRate, "adaptive");
          } else {
            setPlayerRate(expectedRate, "manual");
          }
        }

        attempt += 1;
        if (attempt < retryDelays.length) {
          resumeRateTimerRef.current = window.setTimeout(verifyExpectedRate, retryDelays[attempt]);
        }
      };
      resumeRateTimerRef.current = window.setTimeout(verifyExpectedRate, retryDelays[0]);
    };

    document.addEventListener("visibilitychange", restorePlaybackRate);
    window.addEventListener("pageshow", restorePlaybackRate);
    window.addEventListener("focus", restorePlaybackRate);
    if (player.playing) restorePlaybackRate();
    return () => {
      document.removeEventListener("visibilitychange", restorePlaybackRate);
      window.removeEventListener("pageshow", restorePlaybackRate);
      window.removeEventListener("focus", restorePlaybackRate);
      window.clearTimeout(resumeRateTimerRef.current);
    };
  }, [adaptiveEnabled, effectivePlan, overridden, player.playing, playerReady, refreshPlayer, segmentSpeedOverride, setPlayerRate]);

  const originalDuration = player.duration || effectivePlan?.originalDuration || 0;
  usePlaybackEngagement({
    videoId,
    videoTitle: analysis.video?.title || "YouTube video",
    playing: player.playing,
    currentTime: player.currentTime,
    duration: originalDuration,
    playbackMode: adaptiveEnabled && !overridden ? speedMode : null,
  });
  const sponsorSkipEnd = currentSegment?.sponsor
    ? findSponsorSkipEnd(effectivePlan.segments, currentSegmentIndex)
    : null;
  const seekWithAdaptive = (time) => {
    setSeekTime(null);
    player.seekTo(time);
    lastAppliedRef.current = { segment: -1, speed: 0 };
    if (!effectivePlan || !adaptiveEnabled || overridden) return;
    const destinationIndex = findSegmentIndex(effectivePlan.segments, time);
    const destinationSpeed = effectivePlan.segments[destinationIndex]?.speed;
    if (!destinationSpeed) return;
    desiredAdaptiveRateRef.current = destinationSpeed;
    lastAppliedRef.current = { segment: destinationIndex, speed: destinationSpeed };
    hasAppliedAdaptiveRef.current = true;
    setPlayerRate(destinationSpeed, "adaptive");
  };
  const skipSponsor = () => {
    if (sponsorSkipEnd == null) return;
    // Move just inside the destination segment. This avoids iframe rounding
    // briefly resolving the seek back to the final sponsor frame.
    const destinationTime = Math.min(originalDuration || sponsorSkipEnd + 0.05, sponsorSkipEnd + 0.05);
    seekWithAdaptive(destinationTime);
  };

  const resumeAdaptive = () => {
    adaptiveStateRef.current = true;
    setSegmentSpeedOverride(null);
    setAdaptiveEnabled(true);
    setOverridden(false);
    lastAppliedRef.current = { segment: -1, speed: 0 };
  };
  const setPlaybackMode = (adaptive) => {
    if (adaptive) {
      resumeAdaptive();
      return;
    }
    desiredAdaptiveRateRef.current = null;
    adaptiveStateRef.current = false;
    manualRateRef.current = 1;
    setSegmentSpeedOverride(null);
    setAdaptiveEnabled(false);
    setOverridden(false);
    lastAppliedRef.current = { segment: -1, speed: 0 };
    player.setRate(1, "manual");
  };
  const setManualRate = (rate) => {
    if (adaptiveEnabled && effectivePlan && currentSegmentIndex >= 0) {
      adaptiveStateRef.current = true;
      setSegmentSpeedOverride({ segmentIndex: currentSegmentIndex, rate });
      desiredAdaptiveRateRef.current = rate;
      hasAppliedAdaptiveRef.current = true;
      setOverridden(false);
      lastAppliedRef.current = { segment: -1, speed: 0 };
      return;
    }
    if (rate === 1) {
      setPlaybackMode(false);
      return;
    }
    desiredAdaptiveRateRef.current = null;
    manualRateRef.current = rate;
    setOverridden(true);
    player.setRate(rate, "manual");
  };
  const retry = () => { lastRequestRef.current = ""; runAnalysis(player.duration || undefined, true); };
  const refreshPlan = () => { lastRequestRef.current = ""; runAnalysis(player.duration || undefined, true); };
  const chooseSpeedMode = (mode) => {
    const nextLimits = speedLimitsForMode(mode);
    try {
      window.localStorage.setItem("theofast-playback-mode", mode);
      window.localStorage.setItem("theofast-playback-speeds", JSON.stringify(nextLimits));
    } catch { /* Speed presets still work for this session when storage is unavailable. */ }
    setModeProcessing(true);
    setPendingSpeedMode(mode);
    window.clearTimeout(modeProcessTimerRef.current);
    modeProcessTimerRef.current = window.setTimeout(() => {
      getModePlaybackPlan(videoId, analysis.plan, mode, nextLimits, estimationRates, modePlanCacheRef.current);
      setSpeedMode(mode);
      setSpeedLimits(nextLimits);
      adaptiveStateRef.current = true;
      setAdaptiveEnabled(true);
      setOverridden(false);
      setSegmentSpeedOverride(null);
      lastAppliedRef.current = { segment: -1, speed: 0 };
      setModeProcessing(false);
      setPendingSpeedMode(null);
    }, 650);
    apiRequest("/api/theofast/events/mode", { method: "POST", body: JSON.stringify({ videoId, videoTitle: analysis.video?.title, playbackMode: mode, anonymousId: getAnonymousVisitorId() }) }).catch(() => {});
  };
  const customizeSpeedLimit = (category, rate) => {
    const nextLimits = { ...speedLimits, [category]: rate };
    try {
      window.localStorage.setItem("theofast-playback-mode", "custom");
      window.localStorage.setItem("theofast-playback-speeds", JSON.stringify(nextLimits));
    } catch { /* Custom speeds remain usable for this session when storage is unavailable. */ }
    setModeProcessing(true);
    setPendingSpeedMode("custom");
    window.clearTimeout(modeProcessTimerRef.current);
    modeProcessTimerRef.current = window.setTimeout(() => {
      getModePlaybackPlan(videoId, analysis.plan, "custom", nextLimits, estimationRates, modePlanCacheRef.current);
      setSpeedLimits(nextLimits);
      setSpeedMode("custom");
      adaptiveStateRef.current = true;
      setAdaptiveEnabled(true);
      setOverridden(false);
      setSegmentSpeedOverride(null);
      lastAppliedRef.current = { segment: -1, speed: 0 };
      setModeProcessing(false);
      setPendingSpeedMode(null);
    }, 650);
    apiRequest("/api/theofast/events/mode", { method: "POST", body: JSON.stringify({ videoId, videoTitle: analysis.video?.title, playbackMode: "custom", anonymousId: getAnonymousVisitorId() }) }).catch(() => {});
  };
  const share = async () => {
    const result = await shareVideoLink(versionedShareUrl(window.location.href), title);
    apiRequest("/api/theofast/events/share", {
      method: "POST",
      body: JSON.stringify({ videoId, videoTitle: title, result }),
    }).catch(() => {});
    if (result === "cancelled") return;
    setShareState(result === "shared" ? "Shared" : result === "copied" ? "Link copied" : "Share failed");
    window.setTimeout(() => setShareState("Share"), 1800);
  };

  const title = analysis.video?.title || "YouTube video";
  return (
    <div className="app-shell player-page">
      <Header theme={theme} onToggleTheme={onToggleTheme} onNavigate={onNavigate} player />
      <main className="player-main section-wrap">
        <div className="player-url-bar">
          <div className="player-url-intro">
            <span className="eyebrow">For Theo’s long ones</span>
            <h1>Theo’s videos are long. Your time isn’t.</h1>
            <p>Paste a Theo link. We vary playback speed, keep the full conversation, and cut no words.</p>
          </div>
          <UrlForm onSubmit={(nextVideoId) => onNavigate(`/watch/${nextVideoId}`)} compact />
        </div>
        {videoId ? <>
        <div className="player-layout">
          <div className="player-primary">
            <section ref={immersiveCardRef} className={`video-card ${immersive ? `is-immersive is-${immersiveLayout} ${immersiveControlsVisible ? "controls-visible" : "controls-hidden"}` : ""}`} onClick={toggleImmersiveControlsFromEmpty} onPointerDown={handleImmersivePointerDown} onPointerUp={handleImmersivePointerUp} onPointerCancel={() => { immersiveGestureRef.current = null; }} onFocusCapture={wakeControlsFromChildFocus} role={immersive ? "dialog" : undefined} aria-modal={immersive || undefined} aria-label={immersive ? "Immersive video player" : "YouTube video player"} tabIndex={immersive ? -1 : undefined}>
              {immersive && <div className="immersive-wake-zone" aria-hidden="true" onPointerMove={revealImmersiveControlsOnHover} />}
              {immersive && (
                <div className="immersive-toolbar" onPointerMove={wakeImmersiveControls}>
                  <div className="immersive-layout-toggle" role="group" aria-label="Immersive layout">
                    <button type="button" className={immersiveLayout === "portrait" ? "is-selected" : ""} aria-pressed={immersiveLayout === "portrait"} onClick={() => setImmersiveLayout("portrait")}>Portrait</button>
                    <button type="button" className={immersiveLayout === "landscape" ? "is-selected" : ""} aria-pressed={immersiveLayout === "landscape"} onClick={() => setImmersiveLayout("landscape")}>Landscape</button>
                  </div>
                  <button className="immersive-exit" type="button" onClick={exitImmersive} aria-label="Exit immersive player" title="Exit immersive player">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
                  </button>
                </div>
              )}
              <div className="video-frame">
                <div ref={player.hostRef} />
                {!player.ready && !player.error && <div className="player-loading" aria-live="polite"><span /><p>Loading official player…</p></div>}
                {player.ready && !player.error && (planPending || modeProcessing) && <div className="analysis-loading-overlay" role="status" aria-live="polite" aria-atomic="true">
                  <div className="analysis-loading-message"><svg className="analysis-loading-spinner" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /></svg><span>{modeProcessing ? "Recalculating runtime" : ANALYSIS_LOADING_NOTES[loadingNoteIndex]}</span></div>
                </div>}
                {immersive && <button className="immersive-video-tap-surface" type="button" aria-label={immersiveControlsVisible ? "Hide playback controls" : "Show playback controls"} aria-pressed={immersiveControlsVisible} onClick={toggleImmersiveControlsFromVideo} />}
              </div>
              <PlaybackBar
                segments={effectivePlan?.segments || []}
                currentTime={player.currentTime}
                duration={originalDuration}
                estimatedDuration={effectivePlan?.estimatedDuration}
                playbackRate={player.playbackRate}
                availableRates={player.availableRates}
                playing={player.playing}
                adaptive={adaptiveEnabled && !overridden}
                original={!adaptiveEnabled && !overridden}
                seekTime={seekTime}
                onSeekChange={setSeekTime}
                onSeekCommit={(time) => { seekWithAdaptive(time); setSeekTime(null); }}
                onTogglePlayback={() => player.playing ? player.pause() : player.play()}
                onModeChange={setPlaybackMode}
                onManualRate={setManualRate}
                onToggleFullscreen={enterImmersive}
                isImmersive={immersive}
                sponsorSkipRemaining={sponsorSkipEnd == null ? undefined : Math.max(0, sponsorSkipEnd - player.currentTime)}
                onSkipSponsor={skipSponsor}
                planPending={planPending || modeProcessing}
                onControlsActivity={wakeImmersiveControls}
              />
            </section>
            <section className="video-summary-row" aria-label="Video title and sharing">
              <div className="video-summary-title">
                <strong title={title}>{title}</strong>
                <button type="button" onClick={share} aria-label={shareState === "Share" ? "Share this TheoFast video" : shareState} title={shareState}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15V3m-5 5 5-5 5 5M5 12v7h14v-7" /></svg>
                </button>
              </div>
              <div className="player-feedback-link"><FeedbackLink videoId={videoId} /></div>
            </section>
            {player.error && <div className="notice notice--error" role="alert"><strong>Playback unavailable</strong><span>{player.error}</span></div>}

            {(analysis.status !== "ready" || analysis.video?.fixture) && (
              <div className={`analysis-state notice ${analysis.status === "error" ? "notice--error" : ""}`}>
                <div>
                  <strong>{analysis.video?.fixture ? "Demonstration plan" : analysis.status === "loading" ? "Building playback plan" : analysis.status === "unsupported" ? "Adaptive plan unavailable" : analysis.status === "error" ? "Analysis paused" : "Waiting for video metadata"}</strong>
                  <span>{analysis.video?.fixture ? "This uses real player duration with an explicitly synthetic timing fixture; it is not presented as transcript analysis." : analysis.message}</span>
                </div>
                {["error", "unsupported"].includes(analysis.status) && <button type="button" onClick={retry}>Try again</button>}
              </div>
            )}

          </div>
          <aside className="player-sidebar"><SmartTimeline plan={effectivePlan} currentTime={player.currentTime} onSeek={(time) => seekWithAdaptive(time + 0.05)} speedLimits={speedLimits} speedMode={pendingSpeedMode || speedMode} onSpeedMode={chooseSpeedMode} onSpeedLimit={customizeSpeedLimit} cached={analysis.cached} onRefresh={refreshPlan} processing={modeProcessing} /></aside>
        </div>
        <p className="player-footnote">TheoFast duration assumes detected sponsor sections are skipped. Speed support varies by video and device; unsupported rates fall back toward 1×.</p>
        </> : null}
        <VideoShelf onOpenVideo={(nextVideoId) => onNavigate(`/watch/${nextVideoId}`)} className={videoId ? "video-shelf--after-player" : "video-shelf--home"} />
        <VideoShelf source="history" refreshKey={analysis.plan?.generatedAt} onOpenVideo={(nextVideoId) => onNavigate(`/watch/${nextVideoId}`)} />
        <ExtensionWaitlist />
      </main>
      <SiteFooter usesSponsorBlock={Boolean(videoId && effectivePlan?.communityData === "sponsorblock")} />
    </div>
  );
}
